import React, { useCallback } from "react"
import { keyframes } from "styled-components"
import styled from "~ui/styled"
import { Pressable } from "~/ui/primitives"
import Icon from "~widgets/icon"
import { MODAL_MAX_WIDTH } from "~ui/app-shell"
import { PC_CENTER_PANEL_HEIGHT, type PcPresentationType } from "./pc-presentation"

const backdropIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const centerIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
`

const drawerIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`

const bottomIn = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 80;
  display: flex;
  pointer-events: auto;
`

const CenterOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 24px;
  box-sizing: border-box;
  pointer-events: none;
`

const CenterInlineWrap = styled.div`
  grid-area: 1 / 1;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 24px;
  box-sizing: border-box;
  pointer-events: none;
  min-height: 0;
  overflow: hidden;
`

const Backdrop = styled(Pressable)`
  position: absolute;
  inset: 0;
  border: none;
  margin: 0;
  padding: 0;
  background: rgba(0, 0, 0, 0.45);
  cursor: default;
  animation: ${backdropIn} 0.22s ease;
`

const CenterPanel = styled.div<{ $height?: string }>`
  position: relative;
  z-index: 1;
  width: min(${MODAL_MAX_WIDTH}px, 100%);
  height: ${({ $height }) => $height ?? PC_CENTER_PANEL_HEIGHT};
  max-height: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  background: ${({ theme }) => theme.colors.blockBackground};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  animation: ${centerIn} 0.24s ease;
`

const DrawerPanel = styled.div`
  position: relative;
  z-index: 1;
  width: min(420px, 92%);
  height: 100%;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  animation: ${drawerIn} 0.28s ease;
`

const BottomPanel = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-height: 90%;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.blockBackground};
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  animation: ${bottomIn} 0.28s ease;
`

const ShellHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 48px;
  padding: 0 48px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.blockBackground};
`

const ShellTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`

const CloseButton = styled(Pressable)`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  border: none;
  background: transparent;
  padding: 0 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ShellBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

export default function PcPresentationShell({
  type,
  title,
  onClose,
  showHeader = true,
  centerHeight,
  centerLayout = "overlay",
  children,
}: {
  type: PcPresentationType
  title?: string
  onClose: () => void
  showHeader?: boolean
  centerHeight?: string
  centerLayout?: "overlay" | "inline"
  children: React.ReactNode
}) {
  const stopPropagation = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
  }, [])

  const panel = (() => {
    if (type === "drawer") {
      return (
        <DrawerPanel onClick={stopPropagation}>
          <ShellHeader>
            <CloseButton type="button" aria-label="关闭" onClick={onClose}>
              <Icon name="left" size={16} />
            </CloseButton>
            {title ? <ShellTitle>{title}</ShellTitle> : null}
          </ShellHeader>
          <ShellBody>{children}</ShellBody>
        </DrawerPanel>
      )
    }

    if (type === "bottom") {
      return (
        <BottomPanel onClick={stopPropagation}>
          <ShellBody>{children}</ShellBody>
        </BottomPanel>
      )
    }

    return (
      <CenterPanel $height={centerHeight} onClick={stopPropagation}>
        {showHeader ? (
          <ShellHeader>
            <CloseButton type="button" aria-label="关闭" onClick={onClose}>
              <Icon name="left" size={16} />
            </CloseButton>
            {title ? <ShellTitle>{title}</ShellTitle> : null}
          </ShellHeader>
        ) : null}
        <ShellBody>{children}</ShellBody>
      </CenterPanel>
    )
  })()

  const isCenter = type === "center"

  if (isCenter) {
    const Wrap = centerLayout === "inline" ? CenterInlineWrap : CenterOverlay
    return <Wrap>{panel}</Wrap>
  }

  return (
    <Overlay
      style={{
        alignItems: "stretch",
        justifyContent: type === "bottom" ? "flex-end" : "flex-end",
        flexDirection: type === "bottom" ? "column" : "row",
      }}
    >
      <Backdrop type="button" aria-label="关闭" onClick={onClose} />
      {panel}
    </Overlay>
  )
}
